import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Bot, ExternalLink, FolderGit2, GitBranch, History, Play, RotateCcw, Search, Square } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { Picker } from "../components/Picker.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { EmptyState, Modal, Spinner, ago, sessionEnded, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlStates } from "../lib/useUrlState.js";

/**
 * The working copies under `~/dev`, and the Claude session on each.
 *
 * `~/dev` is development and `/data` is production: a session edits, commits and
 * pushes here, and ops deploys. This is the only screen where a session running in
 * a checkout that is *not* a project can be seen at all — and stopped.
 */
export function Workspaces() {
    const [state, setState] = useState(null);
    // The open history is in the URL too, so the project page can link straight
    // to a working copy's conversations.
    const [{ q: query, history: historyOf }, setUrl] = useUrlStates({ q: "", history: "" });
    const setQuery = (value) => setUrl("q", value);
    const [cloning, setCloning] = useState(false);
    const [busy, setBusy] = useState(null);
    const toast = useToast();

    const load = useCallback(
        () => api.workspaces.list().then(setState).catch(() => setState({ workspaces: [] })),
        []
    );

    // Each load spawns the Claude CLI once and git twice per working copy, so
    // it happens when the sessions watch or a clone says something moved, not
    // on a timer. Idle ↔ busy is a change the watch reports too.
    useLive(["sessions", "workspaces"], load);

    const workspaces = state?.workspaces ?? [];
    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const matching = needle
            ? workspaces.filter((workspace) =>
                `${workspace.name} ${workspace.origin ?? ""} ${workspace.project?.slug ?? ""}`.toLowerCase().includes(needle)
            )
            : workspaces;
        // The ones with a live session lead, so the work in progress is found
        // without searching; each group keeps the server's order.
        return [...matching.filter((workspace) => workspace.session), ...matching.filter((workspace) => !workspace.session)];
    }, [workspaces, query]);

    if (!state) return <Spinner className="muted" />;

    const act = async (name, verb, call) => {
        setBusy(name);
        try {
            await call();
            toast(`${name}: session ${verb}`);
        } catch (error) {
            // Something one command fixes should arrive with the command.
            toast(error.body?.command ? `${error.message} Run: ${error.body.command}` : error.message, "error");
        } finally {
            setBusy(null);
            await load();
        }
    };

    return (
        <>
            <PageHeader
                title="Workspaces"
                meta={query ? `${shown.length} of ${workspaces.length}` : `${workspaces.length} in ~/dev`}
                actions={
                    <>
                        {workspaces.length > 0 ? (
                            <div className="relative w-full sm:w-56">
                                <Search size={14} className="muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
                                <input
                                    className="input pl-8"
                                    placeholder="Search working copies"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    aria-label="Search working copies"
                                />
                            </div>
                        ) : null}
                        <button className="btn-primary px-3 py-1.5" onClick={() => setCloning(true)}>
                            <FolderGit2 size={14} /> New working copy
                        </button>
                    </>
                }
            />

            <DataTable
                rows={shown}
                rowKey={(workspace) => workspace.name}
                empty={
                    <div className="card">
                        <EmptyState
                            icon={FolderGit2}
                            title={workspaces.length === 0 ? "Nothing in ~/dev yet" : "Nothing matches"}
                            hint={
                                workspaces.length === 0
                                    ? "Clone a repository here and a Claude session can work on it. Production stays on the other side of the line."
                                    : "Try a different search."
                            }
                        />
                    </div>
                }
                columns={[
                    {
                        key: "name",
                        header: "Working copy",
                        primary: true,
                        cell: (workspace) => (
                            <span className="flex min-w-0 items-center gap-2">
                                {/* The project's own icon, which is how a row is
                                    recognised before it is read. A directory with no
                                    project renders the same empty box, so the names
                                    still line up. */}
                                <ProjectIcon slug={workspace.project?.slug} hash={workspace.project?.favicon} size={20} framed={Boolean(workspace.project)} />
                                {workspace.project ? (
                                    <Link to={`/projects/${workspace.project.slug}`} className="truncate font-medium hover:underline">
                                        {workspace.name}
                                    </Link>
                                ) : (
                                    <span className="truncate font-medium">{workspace.name}</span>
                                )}
                                {workspace.project?.state === "draft" ? <span className="badge-queued shrink-0">draft</span> : null}
                            </span>
                        )
                    },
                    {
                        key: "session",
                        header: "Session",
                        summary: true,
                        cell: (workspace) =>
                            workspace.session ? (
                                <span className="flex min-w-0 items-center gap-1.5 text-xs">
                                    <span
                                        className={`size-2 shrink-0 rounded-full ${
                                            workspace.session.status === "busy" ? "animate-pulse bg-blue-500" : "bg-green-500"
                                        }`}
                                        aria-hidden
                                    />
                                    <span className="truncate">
                                        {workspace.session.status} · {workspace.session.kind}
                                        {/* The session's own name, when it is not simply the
                                            directory's: a session Claude opened for a task in
                                            a worktree underneath is not the same session. */}
                                        {workspace.session.name && workspace.session.name !== workspace.name
                                            ? ` · ${workspace.session.name}`
                                            : ""}
                                    </span>
                                </span>
                            ) : workspace.lastSession ? (
                                <span className="muted truncate text-xs" title={sessionEnded(workspace.lastSession).why}>
                                    {sessionEnded(workspace.lastSession).label}
                                </span>
                            ) : workspace.trust === "untrusted" ? (
                                // Claude asks once per directory before it will run
                                // there. Starting a session answers it, so this is
                                // the state and not something to go and do.
                                <span className="muted text-xs">none · not trusted yet</span>
                            ) : (
                                <span className="muted text-xs">none</span>
                            )
                    },
                    {
                        key: "branch",
                        header: "Branch",
                        hideBelow: "md",
                        cell: (workspace) =>
                            workspace.branch ? (
                                <span className="flex min-w-0 items-center gap-1.5 text-xs">
                                    <GitBranch size={11} className="muted shrink-0" aria-hidden />
                                    <span className="truncate">{workspace.branch}</span>
                                    {workspace.dirty ? <span className="badge-warn shrink-0">{workspace.dirty} changed</span> : null}
                                    {workspace.ahead ? <span className="badge-queued shrink-0">{workspace.ahead} ahead</span> : null}
                                </span>
                            ) : (
                                <span className="muted text-xs">not a repository</span>
                            )
                    },
                    {
                        key: "project",
                        header: "Project",
                        hideBelow: "lg",
                        cell: (workspace) =>
                            workspace.project ? (
                                // The project's own name, which is often not the
                                // directory's: ~/dev/leaderboard is leaderboard.pt.
                                <Link className="link text-xs" to={`/projects/${workspace.project.slug}`}>
                                    {workspace.project.name || workspace.project.slug}
                                </Link>
                            ) : (
                                <span className="muted text-xs">not deployed by ops</span>
                            )
                    },
                    {
                        key: "started",
                        header: "Started",
                        hideBelow: "xl",
                        cell: (workspace) => <span className="muted text-xs">{workspace.session ? ago(workspace.session.startedAt) : "—"}</span>
                    }
                ]}
                actions={(workspace) => [
                    ...(workspace.session
                        ? workspace.session.kind === "background"
                            ? [
                                {
                                    label: busy === workspace.name ? "Stopping…" : "Stop session",
                                    icon: Square,
                                    danger: true,
                                    busy: busy === workspace.name,
                                    onSelect: () => act(workspace.name, "stopped", () => api.workspaces.stopSession(workspace.name))
                                }
                            ]
                            : [{ label: "Started in a terminal", icon: Bot, onSelect: () => toast("Stop it where it was started", "error") }]
                        : [
                            // Continuing comes first when there is something to
                            // continue: a retired session is the ordinary end of a
                            // day's work, and picking the conversation back up is
                            // what you almost always mean by starting one.
                            ...(workspace.lastSession
                                ? [
                                    {
                                        label: busy === workspace.name ? "Continuing…" : "Continue last session",
                                        icon: RotateCcw,
                                        busy: busy === workspace.name,
                                        onSelect: () =>
                                            act(workspace.name, "continued", () =>
                                                api.workspaces.startSession(workspace.name, workspace.lastSession.sessionId))
                                    }
                                ]
                                : []),
                            {
                                label: busy === workspace.name
                                    ? "Starting…"
                                    : workspace.lastSession ? "Start a new session" : "Start session",
                                icon: Play,
                                busy: busy === workspace.name,
                                onSelect: () => act(workspace.name, "started", () => api.workspaces.startSession(workspace.name))
                            }
                        ]),
                    { label: "History", icon: History, onSelect: () => setUrl("history", workspace.name) }
                ]}
            />

            <SessionHistory
                name={historyOf}
                workspace={workspaces.find((workspace) => workspace.name === historyOf) ?? null}
                onClose={() => setUrl("history", "")}
                onResumed={load}
            />

            <CloneWorkingCopy
                open={cloning}
                onClose={() => setCloning(false)}
                onDone={async (name) => {
                    toast(`~/dev/${name} cloned`);
                    await load();
                }}
            />
        </>
    );
}

/**
 * What a working copy's sessions have been, and the way back into any of them.
 *
 * One entry per conversation, however many times it was resumed: Claude Code
 * copies a resumed conversation into a new session id, and the server folds the
 * copies together by the first message. The title is the one you gave it, else
 * the one Claude gave it from the task, else what you first typed.
 */
function SessionHistory({ name, workspace, onClose, onResumed }) {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(null);
    const toast = useToast();

    useEffect(() => {
        if (!name) return;
        setRows(null);
        api.workspaces
            .history(name)
            .then((result) => setRows(result.sessions))
            .catch((error) => {
                toast(error.message, "error");
                setRows([]);
            });
    }, [name, toast]);

    const resume = async (row) => {
        setBusy(row.sessionId);
        try {
            await api.workspaces.startSession(name, row.sessionId);
            toast(`${name}: "${row.title}" resumed`);
            onClose();
            await onResumed();
        } catch (error) {
            toast(error.message, "error");
        } finally {
            setBusy(null);
        }
    };

    const live = Boolean(workspace?.session);

    return (
        <Modal open={Boolean(name)} onClose={onClose} title={`Sessions · ${name}`} width="max-w-2xl">
            {!rows ? (
                <div className="py-8 text-center"><Spinner className="muted" /></div>
            ) : rows.length === 0 ? (
                <p className="muted py-8 text-center text-sm">No conversations yet. A session that was never spoken to leaves nothing to resume.</p>
            ) : (
                <ul className="max-h-[28rem] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
                    {rows.map((row) => (
                        <li key={row.sessionId} className="flex min-w-0 items-start gap-3 px-3 py-3">
                            <div className="min-w-0 flex-1">
                                <p className="flex min-w-0 items-center gap-2">
                                    <span className="truncate text-sm font-medium">{row.title}</span>
                                    <SessionState state={row.state} />
                                </p>
                                {/* The automated title, when it is not already the title:
                                    it says what the conversation was about better than the
                                    project's name does. */}
                                {row.aiTitle && row.aiTitle !== row.title ? <p className="muted truncate text-xs">{row.aiTitle}</p> : null}
                                {row.lastPrompt ? (
                                    <p className="muted mt-1 truncate text-xs" title={row.lastPrompt}>
                                        <span className="text-gray-400 dark:text-gray-500">last:</span> {row.lastPrompt}
                                    </p>
                                ) : null}
                                <p className="muted mt-1 flex flex-wrap gap-x-2 text-xs tabular-nums">
                                    <span>{row.turns} turn{row.turns === 1 ? "" : "s"}</span>
                                    {row.gitBranch ? <span className="flex items-center gap-1"><GitBranch size={10} aria-hidden /> {row.gitBranch}</span> : null}
                                    {row.worktree ? <span>worktree {row.worktree}</span> : null}
                                    {row.cost ? (
                                        <span>
                                            ${row.cost.usd.toFixed(2)}
                                            {row.cost.linesAdded || row.cost.linesRemoved ? ` · +${row.cost.linesAdded} −${row.cost.linesRemoved}` : ""}
                                        </span>
                                    ) : null}
                                    <span title={row.startedAt ? `started ${new Date(row.startedAt).toLocaleString()}` : ""}>{ago(row.lastAt)}</span>
                                    {row.resumed ? <span>resumed {row.resumed}×</span> : null}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                                {row.remoteUrl ? (
                                    <a href={row.remoteUrl} target="_blank" rel="noreferrer" className="btn-ghost px-2.5 py-1" title="Open in the Claude app">
                                        <ExternalLink size={12} /> Open
                                    </a>
                                ) : null}
                                {row.state !== "running" ? (
                                    <button
                                        className="btn-ghost px-2.5 py-1"
                                        disabled={live || busy === row.sessionId}
                                        title={live ? "Stop the running session first" : "Bring this conversation back, in the background"}
                                        onClick={() => resume(row)}
                                    >
                                        {busy === row.sessionId ? <Spinner size={12} /> : <RotateCcw size={12} />} Resume
                                    </button>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </Modal>
    );
}

function SessionState({ state }) {
    if (state === "running") return <span className="badge-running shrink-0">running</span>;
    if (state === "done") return <span className="badge-success shrink-0">done</span>;
    if (state === "stopped") return <span className="badge-queued shrink-0">stopped</span>;
    return null;
}

/**
 * Clone a repository into `~/dev/<name>`.
 *
 * Every repository the token can see, not only the onboarded ones: a working copy
 * is where a project starts, so it must be possible to have one before ops knows
 * anything about it.
 */
export function CloneWorkingCopy({ open, onClose, onDone }) {
    const [repo, setRepo] = useState(null);
    const [name, setName] = useState("");
    const [picking, setPicking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setRepo(null);
        setName("");
        setError(null);
        setPicking(true);
    }, [open]);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.workspaces.clone(name.trim(), repo.ssh);
            onClose();
            await onDone(name.trim());
        } catch (problem) {
            setError(problem.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Modal open={open && !picking} onClose={onClose} title="New working copy">
                <div className="space-y-4">
                    <div>
                        <span className="label">Repository</span>
                        <button className="btn-ghost mt-1 w-full justify-between px-3 py-2" onClick={() => setPicking(true)}>
                            <span className="truncate">{repo ? repo.path : "Choose a repository"}</span>
                            <GitBranch size={13} className="shrink-0" aria-hidden />
                        </button>
                    </div>

                    <label className="block">
                        <span className="label">Directory</span>
                        <input className="input mt-1" value={name} onChange={(event) => setName(event.target.value)} placeholder="qto" />
                        <span className="muted mt-1 block text-xs">
                            Cloned to <code className="font-mono">~/dev/{name || "<name>"}</code>. An existing one is never cloned over.
                        </span>
                    </label>

                    {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}

                    <div className="flex justify-end gap-2">
                        <button className="btn-ghost px-3 py-1.5" onClick={onClose}>Cancel</button>
                        <button className="btn-primary px-3 py-1.5" disabled={!repo || !name.trim() || busy} onClick={submit}>
                            {busy ? <Spinner size={13} /> : <FolderGit2 size={14} />} {busy ? "Cloning…" : "Clone"}
                        </button>
                    </div>
                </div>
            </Modal>

            <RepoPicker
                open={open && picking}
                onClose={() => setPicking(false)}
                onPick={(chosen) => {
                    setRepo(chosen);
                    // The directory is named after the repository unless you say
                    // otherwise, which is what makes a session match its project.
                    if (!name) setName(chosen.path.split("/").pop().replace(/\.git$/, "").toLowerCase());
                }}
            />
        </>
    );
}

/** Every repository the token can see, so a source is chosen rather than typed. */
export function RepoPicker({ open, onClose, onPick }) {
    return (
        <Picker
            open={open}
            onClose={onClose}
            title="Repositories"
            hint="Everything this GitLab token can see, most recently active first."
            load={() =>
                api.discover.repos().then((repos) =>
                    repos.map((repo) => ({
                        id: repo.id,
                        label: repo.path,
                        detail: `${repo.branch ?? "no default branch"}${repo.onboarded ? "" : " · not onboarded"}`,
                        badge: repo.onboarded ? "onboarded" : null,
                        value: repo
                    }))
                )
            }
            onPick={(item) => onPick(item.value)}
        />
    );
}
